import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Column,
  Divider,
  FieldValue,
  InputOfficeDropdown,
  InputSelect,
  Label,
  Row,
} from '~/commonComponents';
import type { ISelectOption } from '~/commonComponents/InputSelect';
import { EXCLUDED_PROFESSIONS } from '~/components/PermanentJobsV2/Constants';
import { jobTitleMaxLength } from '~/components/PermanentJobsV2/utils';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import {
  useIsParentOfficeSelector,
  useProfessionOptions,
  useSpecialtyOptions,
  useUserSelector,
  useUserSpecialty,
} from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ProfessionTypeEnum } from '~/models';

export const BasicInfoForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);
  const isParentOffice = useIsParentOfficeSelector();
  const currentUser = useUserSelector();
  const userSpecialty = useUserSpecialty();

  const watchOfficeSpecialty = useWatch({ control, name: 'officeSpecialty' });
  const watchProfessionType = useWatch({ control, name: 'professionType' });

  const { options: professionOptions } = useProfessionOptions({ excludes: EXCLUDED_PROFESSIONS });
  const optProfessionTypes = professionOptions.map(
    ({ label, value }) => ({ title: label, value }) as ISelectOption,
  );

  const { isLoading: isGetUserEducationLoading, options: optSpecialty } = useSpecialtyOptions(
    watchProfessionType || ProfessionTypeEnum.RDH,
  );

  useEffect(() => {
    if (optSpecialty.length === 0) return;

    const isCurrentSpecialtyValid = optSpecialty.some(
      (option) => option.value === watchOfficeSpecialty,
    );

    if (!isCurrentSpecialtyValid) {
      const defaultSpecialty = optSpecialty.some((option) => option.value === userSpecialty)
        ? userSpecialty
        : optSpecialty[0]?.value;

      if (defaultSpecialty) {
        setValue?.('officeSpecialty', defaultSpecialty, { shouldValidate: true });
      }
    }
  }, [watchOfficeSpecialty, setValue, watchProfessionType, optSpecialty, userSpecialty]);

  return (
    <Column gap={24}>
      {(!isEnforcePostAJobFunnelImprovement || isParentOffice) && (
        <>
          <Column>
            <Label text="Office Name" required={isParentOffice} />
            {isParentOffice ? (
              <Controller
                control={control}
                name="office"
                render={({ field }) => (
                  <InputOfficeDropdown
                    placeholder="Enter or Select Office Name"
                    value={field.value || ''}
                    onChange={(item) => field.onChange(item?.value)}
                    error={!!errors?.office}
                  />
                )}
              />
            ) : (
              <Typography as="span" style={{ paddingLeft: 17 }}>{currentUser?.office_name || ''}</Typography>
            )}
          </Column>
          <Divider height={1} marginVertical={0} />
        </>
      )}

      <Column>
        <Controller
          control={control}
          name="jobTitle"
          render={({ field }) => (
            <FieldValue
              label="Job Title"
              placeholder="Enter Job Title"
              required
              shouldCapitalize={false}
              shouldTrim={true}
              disableEmoji={true}
              value={field.value}
              setValue={field.onChange}
              customInputStyle={{ width: '100%', boxSizing: 'border-box' }}
              validation={{ maxLength: jobTitleMaxLength }}
              error={!!errors?.jobTitle}
              errorStyle="border"
              dataCy="perm-job-title-input"
            />
          )}
        />
        <Typography italic color={colors.neutral[500]} style={{ marginTop: 8 }}>
          {jobTitleMaxLength} Character Max
        </Typography>
      </Column>

      {!isEnforcePostAJobFunnelImprovement && <Divider height={1} marginVertical={0} />}

      {isEnforcePostAJobFunnelImprovement ? (
        <Row gap={24}>
          <Column flex={1}>
            <Label text="Professional Type" required />
            <Controller
              control={control}
              name="professionType"
              render={({ field }) => (
                <InputSelect
                  placeholder="Select Professional Type"
                  value={field.value}
                  setValue={field.onChange}
                  isSelectVisible={false}
                  options={optProfessionTypes}
                  error={!!errors?.professionType}
                  dataCy="perm-profession-type-select"
                />
              )}
            />
          </Column>

          <Column flex={1}>
            <Label text="Specialty" required />
            <Controller
              control={control}
              name="officeSpecialty"
              render={({ field }) => (
                <InputSelect
                  placeholder="Select Specialty"
                  value={field.value}
                  setValue={field.onChange}
                  isSelectVisible={false}
                  options={optSpecialty}
                  customTitle={field.value ? field.value.split('_')?.[1] || 'General' : undefined}
                  error={!!errors?.officeSpecialty}
                  isLoading={isGetUserEducationLoading}
                  dataCy="perm-specialty-select"
                />
              )}
            />
          </Column>
        </Row>
      ) : (
        <>
          <Column>
            <Label text="Professional Type" required />
            <Controller
              control={control}
              name="professionType"
              render={({ field }) => (
                <InputSelect
                  placeholder="Select Professional Type"
                  value={field.value}
                  setValue={field.onChange}
                  isSelectVisible={false}
                  options={optProfessionTypes}
                  error={!!errors?.professionType}
                  dataCy="perm-profession-type-select"
                />
              )}
            />
          </Column>

          <Column>
            <Label text="Specialty" required />
            <Controller
              control={control}
              name="officeSpecialty"
              render={({ field }) => (
                <InputSelect
                  placeholder="Select Specialty"
                  value={field.value}
                  setValue={field.onChange}
                  isSelectVisible={false}
                  options={optSpecialty}
                  customTitle={field.value ? field.value.split('_')?.[1] || 'General' : undefined}
                  error={!!errors?.officeSpecialty}
                  isLoading={isGetUserEducationLoading}
                  dataCy="perm-specialty-select"
                />
              )}
            />
          </Column>
        </>
      )}
    </Column>
  );
};
